import React, { useEffect, useMemo, useState } from "react";
import type { PlayerGameLogPosition, PlayerGameLogScoring, PlayerSeasonGameLogs } from "../../shared/data/playerGameLogs";
import { getComparableSeasonLogs } from "../../shared/utils/playerStatBaselines";
import { PLAYER_PROFILE_STAT_COLOR_CODING_ENABLED, buildHeatmapScales, getHeatmapCellStyle, getHeatmapTier } from "../../shared/utils/playerStatHeatmap";
import {
  PLAYER_STATS_GRID_DEFAULT_ZOOM_INDEX,
  PlayerStatsGridZoomControl,
  getPlayerStatsGridLayout,
} from "./playerStatsGridLayout";

type Props = {
  seasons: PlayerSeasonGameLogs[];
  position?: PlayerGameLogPosition | null;
  selectedScoring: PlayerGameLogScoring;
  onSelectedScoringChange: (scoring: PlayerGameLogScoring) => void;
  statsView: "logs" | "career";
  onStatsViewChange: (view: "logs" | "career") => void;
  gridZoomIndex?: number;
  onGridZoomIndexChange?: React.Dispatch<React.SetStateAction<number>>;
};

type DisplayMode = "averages" | "totals";
type StatTabKey = "passing" | "rushing" | "receiving" | "misc";

type CareerRow = {
  season: number;
  gamesPlayed: number;
  pprPoints: number;
  standardPoints: number;
  halfPprPoints: number;
  passAttempts: number;
  passYards: number;
  passTds: number;
  completions: number;
  interceptions: number;
  sacks: number;
  rushAttempts: number;
  rushYards: number;
  rushTds: number;
  targets: number;
  receptions: number;
  receivingYards: number;
  receivingTds: number;
  fumbles: number;
  fumblesLost: number;
  kickoffReturns: number;
  kickoffReturnYards: number;
  puntReturns: number;
  specialTeamsTds: number;
};

type Column = {
  key: string;
  label: string;
  align?: "left" | "center";
  lowerIsBetter?: boolean;
  getValue: (row: CareerRow, mode: DisplayMode, scoring: PlayerGameLogScoring) => number | string | undefined;
  digits?: number;
  formatter?: (value: number | string | undefined) => string;
};

type ColumnGroup = {
  label: string;
  columns: Column[];
};

function sum(values: Array<number | undefined>) {
  return values.reduce<number>((total, value) => total + (value ?? 0), 0);
}

function round(value: number, digits = 1) {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function perGame(total: number, gamesPlayed: number, digits = 1) {
  if (!gamesPlayed) return 0;
  return round(total / gamesPlayed, digits);
}

function formatMetric(value: number | string | undefined, digits = 1) {
  if (value == null) return "-";
  if (typeof value === "string") return value;
  if (!Number.isFinite(value)) return "-";
  if (value === 0) return "0";
  return Number.isInteger(value) ? String(value) : value.toFixed(digits);
}

function summarizeSeason(season: PlayerSeasonGameLogs): CareerRow {
  const rows = season.rows.filter((row) => row.week > 0);
  const gamesPlayed = rows.length;

  return {
    season: season.season,
    gamesPlayed,
    pprPoints: sum(rows.map((row) => row.fantasyPointsPpr ?? row.fantasyPoints)),
    standardPoints: sum(rows.map((row) => row.fantasyPointsStandard ?? row.fantasyPoints)),
    halfPprPoints: sum(rows.map((row) => row.fantasyPointsHalfPpr ?? row.fantasyPoints)),
    passAttempts: sum(rows.map((row) => row.passingAttempts)),
    passYards: sum(rows.map((row) => row.passingYards)),
    passTds: sum(rows.map((row) => row.passingTds)),
    completions: sum(rows.map((row) => row.passingCompletions)),
    interceptions: sum(rows.map((row) => row.passingInterceptions)),
    sacks: sum(rows.map((row) => row.sacks)),
    rushAttempts: sum(rows.map((row) => row.rushAttempts)),
    rushYards: sum(rows.map((row) => row.rushYards)),
    rushTds: sum(rows.map((row) => row.rushTds)),
    targets: sum(rows.map((row) => row.targets)),
    receptions: sum(rows.map((row) => row.receptions)),
    receivingYards: sum(rows.map((row) => row.receivingYards)),
    receivingTds: sum(rows.map((row) => row.receivingTds)),
    fumbles: sum(rows.map((row) => row.fumbles)),
    fumblesLost: sum(rows.map((row) => row.fumblesLost)),
    kickoffReturns: sum(rows.map((row) => row.kickoffReturns)),
    kickoffReturnYards: sum(rows.map((row) => row.kickoffReturnYards)),
    puntReturns: sum(rows.map((row) => row.puntReturns)),
    specialTeamsTds: sum(rows.map((row) => row.specialTeamsTds)),
  };
}

function pointsForScoring(row: CareerRow, scoring: PlayerGameLogScoring) {
  if (scoring === "standard") return row.standardPoints;
  if (scoring === "halfPpr") return row.halfPprPoints;
  return row.pprPoints;
}

function getColumns(position: PlayerGameLogPosition | null | undefined): Column[] {
  const base: Column[] = [
    {
      key: "year",
      label: "Year",
      align: "left",
      getValue: (row) => row.season,
      formatter: (value) => String(value ?? "-"),
    },
    {
      key: "gp",
      label: "GP",
      getValue: (row) => row.gamesPlayed,
    },
    {
      key: "fpts",
      label: "FPTS",
      getValue: (row, mode, scoring) =>
        mode === "averages"
          ? perGame(pointsForScoring(row, scoring), row.gamesPlayed, 2)
          : round(pointsForScoring(row, scoring), 1),
        digits: 2,
      },
  ];

  if (position === "QB") {
    return [
      ...base,
      {
        key: "att",
        label: "ATT",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.passAttempts, row.gamesPlayed, 1) : row.passAttempts),
        digits: 1,
      },
      {
        key: "pass_yd",
        label: "YD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.passYards, row.gamesPlayed, 1) : row.passYards),
        digits: 1,
      },
      {
        key: "pass_td",
        label: "TD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.passTds, row.gamesPlayed, 1) : row.passTds),
        digits: 1,
      },
      {
        key: "cmp_pct",
        label: "CMP%",
        getValue: (row) => {
          if (!row.passAttempts) return 0;
          return round((row.completions / row.passAttempts) * 100, 1);
        },
        digits: 1,
      },
      {
        key: "ypa",
        label: "YPA",
        getValue: (row) => (row.passAttempts ? round(row.passYards / row.passAttempts, 1) : 0),
        digits: 1,
      },
      {
        key: "int",
        label: "INT",
        lowerIsBetter: true,
        getValue: (row, mode) => (mode === "averages" ? perGame(row.interceptions, row.gamesPlayed, 1) : row.interceptions),
        digits: 1,
      },
      {
        key: "sacks",
        label: "SACK",
        lowerIsBetter: true,
        getValue: (row, mode) => (mode === "averages" ? perGame(row.sacks, row.gamesPlayed, 1) : row.sacks),
        digits: 1,
      },
      {
        key: "car",
        label: "CAR",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.rushAttempts, row.gamesPlayed, 1) : row.rushAttempts),
        digits: 1,
      },
      {
        key: "rush_yd",
        label: "RUSH YD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.rushYards, row.gamesPlayed, 1) : row.rushYards),
        digits: 1,
      },
      {
        key: "rush_td",
        label: "RUSH TD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.rushTds, row.gamesPlayed, 1) : row.rushTds),
        digits: 1,
      },
    ] as Column[];
  }

  if (position === "RB") {
    return [
      ...base,
      {
        key: "car",
        label: "CAR",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.rushAttempts, row.gamesPlayed, 1) : row.rushAttempts),
        digits: 1,
      },
      {
        key: "rush_yd",
        label: "RUSH YD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.rushYards, row.gamesPlayed, 1) : row.rushYards),
        digits: 1,
      },
      {
        key: "ypc",
        label: "YPC",
        getValue: (row) => (row.rushAttempts ? round(row.rushYards / row.rushAttempts, 1) : 0),
        digits: 1,
      },
      {
        key: "rush_td",
        label: "RUSH TD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.rushTds, row.gamesPlayed, 1) : row.rushTds),
        digits: 1,
      },
      {
        key: "targets",
        label: "TAR",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.targets, row.gamesPlayed, 1) : row.targets),
        digits: 1,
      },
      {
        key: "rec",
        label: "REC",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.receptions, row.gamesPlayed, 1) : row.receptions),
        digits: 1,
      },
      {
        key: "rec_yd",
        label: "REC YD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.receivingYards, row.gamesPlayed, 1) : row.receivingYards),
        digits: 1,
      },
      {
        key: "rec_td",
        label: "REC TD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.receivingTds, row.gamesPlayed, 1) : row.receivingTds),
        digits: 1,
      },
    ] as Column[];
  }

  return [
    ...base,
    {
      key: "targets",
      label: "TAR",
      getValue: (row, mode) => (mode === "averages" ? perGame(row.targets, row.gamesPlayed, 1) : row.targets),
      digits: 1,
    },
    {
      key: "rec",
      label: "REC",
      getValue: (row, mode) => (mode === "averages" ? perGame(row.receptions, row.gamesPlayed, 1) : row.receptions),
      digits: 1,
    },
    {
      key: "catch_rate",
      label: "CATCH%",
      getValue: (row) => (row.targets ? round((row.receptions / row.targets) * 100, 1) : 0),
      digits: 1,
    },
    {
      key: "rec_yd",
      label: "YD",
      getValue: (row, mode) => (mode === "averages" ? perGame(row.receivingYards, row.gamesPlayed, 1) : row.receivingYards),
      digits: 1,
    },
    {
      key: "ypr",
      label: "YPR",
      getValue: (row) => (row.receptions ? round(row.receivingYards / row.receptions, 1) : 0),
      digits: 1,
    },
    {
      key: "rec_td",
      label: "TD",
      getValue: (row, mode) => (mode === "averages" ? perGame(row.receivingTds, row.gamesPlayed, 1) : row.receivingTds),
      digits: 1,
    },
    {
      key: "rush_att",
      label: "RUSH",
      getValue: (row, mode) => (mode === "averages" ? perGame(row.rushAttempts, row.gamesPlayed, 1) : row.rushAttempts),
      digits: 1,
    },
    {
      key: "rush_yd",
      label: "RUSH YD",
      getValue: (row, mode) => (mode === "averages" ? perGame(row.rushYards, row.gamesPlayed, 1) : row.rushYards),
      digits: 1,
    },
  ] as Column[];
}

function getColumnGroups(position: PlayerGameLogPosition | null | undefined): ColumnGroup[] {
  const columns = getColumns(position);
  const byKey = Object.fromEntries(columns.map((column) => [column.key, column]));
  const pick = (keys: string[]) => keys.map((key) => byKey[key]).filter(Boolean);
  const baseGroups: ColumnGroup[] = [
    { label: "", columns: pick(["year", "gp"]) },
    { label: "Fantasy", columns: pick(["fpts"]) },
  ];
  const fumbleGroup: ColumnGroup = {
    label: "Fumble",
    columns: [
      {
        key: "fumbles",
        label: "FUM",
        lowerIsBetter: true,
        getValue: (row, mode) => (mode === "averages" ? perGame(row.fumbles, row.gamesPlayed, 1) : row.fumbles),
        digits: 1,
      },
      {
        key: "fumbles_lost",
        label: "LOST",
        lowerIsBetter: true,
        getValue: (row, mode) => (mode === "averages" ? perGame(row.fumblesLost, row.gamesPlayed, 1) : row.fumblesLost),
        digits: 1,
      },
    ],
  };
  const returningGroup: ColumnGroup = {
    label: "Returning",
    columns: [
      {
        key: "kick_returns",
        label: "KR",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.kickoffReturns, row.gamesPlayed, 1) : row.kickoffReturns),
        digits: 1,
      },
      {
        key: "kick_return_yd",
        label: "KYD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.kickoffReturnYards, row.gamesPlayed, 1) : row.kickoffReturnYards),
        digits: 1,
      },
      {
        key: "punt_returns",
        label: "PR",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.puntReturns, row.gamesPlayed, 1) : row.puntReturns),
        digits: 1,
      },
      {
        key: "special_teams_td",
        label: "SP TD",
        getValue: (row, mode) => (mode === "averages" ? perGame(row.specialTeamsTds, row.gamesPlayed, 1) : row.specialTeamsTds),
        digits: 1,
      },
    ],
  };

  if (position === "QB") {
    return [
      ...baseGroups,
      { label: "Pass", columns: pick(["att", "pass_yd", "pass_td", "cmp_pct", "ypa", "int"]) },
      { label: "Rushing", columns: pick(["car", "rush_yd", "rush_td"]) },
      { label: "Sacked", columns: pick(["sacks"]) },
      fumbleGroup,
    ];
  }

  if (position === "RB") {
    return [
      ...baseGroups,
      { label: "Rushing", columns: pick(["car", "rush_yd", "ypc", "rush_td"]) },
      { label: "Receiving", columns: pick(["targets", "rec", "rec_yd", "rec_td"]) },
      fumbleGroup,
      returningGroup,
    ];
  }

  return [
    ...baseGroups,
    { label: "Receiving", columns: pick(["targets", "rec", "catch_rate", "rec_yd", "ypr", "rec_td"]) },
    { label: "Rushing", columns: pick(["rush_att", "rush_yd"]) },
    fumbleGroup,
    returningGroup,
  ];
}

function getStatTabs(position: PlayerGameLogPosition | null | undefined): Array<{ key: StatTabKey; label: string }> {
  if (position === "QB") {
    return [
      { key: "passing", label: "Passing" },
      { key: "rushing", label: "Rushing" },
      { key: "misc", label: "Misc" },
    ];
  }

  if (position === "RB") {
    return [
      { key: "rushing", label: "Rushing" },
      { key: "receiving", label: "Receiving" },
      { key: "misc", label: "Misc" },
    ];
  }

  return [
    { key: "receiving", label: "Receiving" },
    { key: "rushing", label: "Rushing" },
    { key: "misc", label: "Misc" },
  ];
}

function getVisibleColumnGroups(
  allGroups: ColumnGroup[],
  selectedTab: StatTabKey,
  position: PlayerGameLogPosition | null | undefined
): ColumnGroup[] {
  const baseGroups = allGroups.slice(0, 2);
  const extraGroups = allGroups.slice(2);

  let targetLabel = "";
  if (selectedTab === "passing") targetLabel = "Pass";
  else if (selectedTab === "rushing") targetLabel = "Rushing";
  else if (selectedTab === "receiving") targetLabel = "Receiving";
  else if (selectedTab === "misc") {
    if (position === "QB") {
      return [...baseGroups, ...extraGroups.filter((group) => group.label === "Sacked" || group.label === "Fumble")];
    }
    return [...baseGroups, ...extraGroups.filter((group) => group.label === "Fumble" || group.label === "Returning")];
  }

  const targetGroup = extraGroups.find((group) => group.label === targetLabel);
  return targetGroup ? [...baseGroups, targetGroup] : baseGroups;
}

export default function PlayerCareerTable({
  seasons,
  position,
  selectedScoring,
  onSelectedScoringChange,
  statsView,
  onStatsViewChange,
  gridZoomIndex: gridZoomIndexProp,
  onGridZoomIndexChange,
}: Props) {
  const rootRef = React.useRef<HTMLElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1280 : window.innerWidth
  );
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === "undefined" ? 844 : window.innerHeight
  );
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [displayMode, setDisplayMode] = useState<DisplayMode>("averages");
  const [gridZoomIndexInternal, setGridZoomIndexInternal] = useState(PLAYER_STATS_GRID_DEFAULT_ZOOM_INDEX);
  const statTabs = useMemo(() => getStatTabs(position), [position]);
  const [selectedTab, setSelectedTab] = useState<StatTabKey>(statTabs[0]?.key ?? "misc");
  const gridZoomIndex = gridZoomIndexProp ?? gridZoomIndexInternal;
  const setGridZoomIndex = onGridZoomIndexChange ?? setGridZoomIndexInternal;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const element = rootRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setContainerWidth(element.getBoundingClientRect().width);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(element);
    return () => observer.disconnect();
  }, [displayMode, selectedScoring, statsView]);

  useEffect(() => {
    setSelectedTab(statTabs[0]?.key ?? "misc");
  }, [statTabs]);

  const responsiveWidth = containerWidth || viewportWidth;
  const mobileMode = responsiveWidth <= 1100;
  const compactTableMode = responsiveWidth <= 760;
  const profileControlsLandscape = mobileMode && viewportWidth > viewportHeight;
  const profileControlFontSize = profileControlsLandscape ? 10 : mobileMode ? 11 : 12;
  const profileSmallControlFontSize = profileControlsLandscape ? 9 : mobileMode ? 10 : 12;
  const profileControlPaddingY = profileControlsLandscape ? 3 : mobileMode ? 5 : 6;
  const profileControlPaddingX = profileControlsLandscape ? 7 : mobileMode ? 10 : 12;
  const profileSmallControlPaddingX = profileControlsLandscape ? 6 : mobileMode ? 8 : 10;
  const profileControlMinHeight = profileControlsLandscape ? 24 : undefined;
  const profileControlGap = profileControlsLandscape ? 4 : 8;
  const rows = useMemo(
    () => [...seasons].map(summarizeSeason).sort((a, b) => b.season - a.season),
    [seasons]
  );
  const columnGroups = useMemo(
    () => getVisibleColumnGroups(getColumnGroups(position), selectedTab, position),
    [position, selectedTab]
  );
  const columns = useMemo(() => columnGroups.flatMap((group) => group.columns), [columnGroups]);
  const heatmapScalesBySeason = useMemo(() => {
    return Object.fromEntries(
      rows.map((row) => {
        const comparableRows = getComparableSeasonLogs(position, row.season).map(summarizeSeason);
        const scales = buildHeatmapScales(
          comparableRows,
          columns
            .filter((column) => column.key !== "year")
            .map((column) => ({
              key: column.key,
              lowerIsBetter: column.lowerIsBetter,
              getNumericValue: (careerRow: CareerRow) => {
                const value = column.getValue(careerRow, displayMode, selectedScoring);
                return typeof value === "number" ? value : undefined;
              },
            }))
        );

        return [row.season, scales];
      })
    );
  }, [columns, displayMode, position, rows, selectedScoring]);
  const showScoringTabs = position !== "QB";
  const {
    firstColumnWidth,
    secondColumnWidth,
    statColumnWidth,
    headerFontSize,
    cellFontSize,
    cellPadding,
    statPillPadding,
    statPillRadius,
  } = getPlayerStatsGridLayout({ mobileMode, compactTableMode, gridZoomIndex });

  return (
    <section
      ref={rootRef}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: mobileMode ? 8 : 10,
        minWidth: 0,
        minHeight: 0,
        height: mobileMode ? "100%" : undefined,
        flex: mobileMode ? "1 1 auto" : undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: profileControlGap,
          flexWrap: "wrap",
          padding: profileControlsLandscape ? "6px 12px 0" : mobileMode ? "10px 14px 0" : 0,
          minWidth: 0,
          paddingBottom: profileControlsLandscape ? 2 : mobileMode ? 4 : undefined,
          overflow: "visible",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            gap: 2,
            padding: 2,
            borderRadius: 999,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.08)",
            flex: mobileMode ? "0 0 auto" : undefined,
            minWidth: mobileMode ? "max-content" : undefined,
          }}
        >
          {([
            { key: "logs", label: "Logs" },
            { key: "career", label: "Career" },
          ] as const).map((option) => {
            const isActive = option.key === statsView;
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => onStatsViewChange(option.key)}
                style={{
                  appearance: "none",
                  border: "none",
                  cursor: "pointer",
                  borderRadius: 999,
                  padding: `${profileControlPaddingY}px ${profileControlPaddingX}px`,
                  background: isActive ? "rgba(255,255,255,0.92)" : "transparent",
                  color: isActive ? "#0f172a" : "rgba(255,255,255,0.82)",
                  fontWeight: 900,
                  fontSize: profileControlFontSize,
                  minHeight: profileControlMinHeight,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: profileControlGap,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flex: mobileMode ? "0 0 auto" : undefined,
            minWidth: mobileMode ? "max-content" : undefined,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              gap: 2,
              padding: 2,
              borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.08)",
              flex: mobileMode ? "0 0 auto" : undefined,
              minWidth: mobileMode ? "max-content" : 0,
            }}
          >
            {statTabs.map((tab) => {
              const isActive = tab.key === selectedTab;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setSelectedTab(tab.key)}
                  style={{
                    appearance: "none",
                    border: "none",
                    cursor: "pointer",
                    borderRadius: 999,
                    padding: `${profileControlPaddingY}px ${profileSmallControlPaddingX}px`,
                    background: isActive ? "rgba(255,255,255,0.92)" : "transparent",
                    color: isActive ? "#0f172a" : "rgba(255,255,255,0.82)",
                    fontWeight: 900,
                    fontSize: profileSmallControlFontSize,
                    minHeight: profileControlMinHeight,
                    lineHeight: 1,
                    flex: mobileMode ? "0 0 auto" : undefined,
                    minWidth: mobileMode ? "max-content" : 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {mobileMode
                    ? tab.label === "Receiving"
                      ? "Rec"
                      : tab.label === "Rushing"
                        ? "Rush"
                        : tab.label
                    : tab.label}
                </button>
              );
            })}
          </div>

          <div
            style={{
              display: "inline-flex",
              gap: 2,
              padding: 2,
              borderRadius: 999,
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.08)",
              flex: mobileMode ? "0 0 auto" : undefined,
              minWidth: mobileMode ? "max-content" : undefined,
            }}
          >
            {([
              { key: "averages", label: "Averages" },
              { key: "totals", label: "Totals" },
            ] as const).map((option) => {
              const isActive = option.key === displayMode;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setDisplayMode(option.key)}
                  style={{
                    appearance: "none",
                    border: "none",
                    cursor: "pointer",
                    borderRadius: 999,
                    padding: `${profileControlPaddingY}px ${profileControlPaddingX}px`,
                    background: isActive ? "rgba(255,255,255,0.92)" : "transparent",
                    color: isActive ? "#0f172a" : "rgba(255,255,255,0.82)",
                    fontWeight: 900,
                    fontSize: profileControlFontSize,
                    minHeight: profileControlMinHeight,
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          {showScoringTabs ? (
            <div
              style={{
                display: "inline-flex",
                gap: 2,
                padding: 2,
                borderRadius: 999,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.08)",
                flex: mobileMode ? "0 0 auto" : undefined,
                minWidth: mobileMode ? "max-content" : undefined,
              }}
            >
              {[
                { key: "standard", label: "STD" },
                { key: "halfPpr", label: "HALF-PPR" },
                { key: "ppr", label: "PPR" },
              ].map((option) => {
                const isActive = option.key === selectedScoring;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => onSelectedScoringChange(option.key as PlayerGameLogScoring)}
                    style={{
                      appearance: "none",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 999,
                      padding: `${profileControlPaddingY}px ${profileSmallControlPaddingX}px`,
                      background: isActive ? "rgba(255,255,255,0.92)" : "transparent",
                      color: isActive ? "#0f172a" : "rgba(255,255,255,0.82)",
                      fontWeight: 900,
                      fontSize: profileSmallControlFontSize,
                      minHeight: profileControlMinHeight,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {mobileMode && option.key === "halfPpr" ? "HALF" : option.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          <PlayerStatsGridZoomControl
            gridZoomIndex={gridZoomIndex}
            setGridZoomIndex={setGridZoomIndex}
            label="career"
            mobileMode={mobileMode}
            compactMode={profileControlsLandscape}
          />
        </div>
      </div>

      <div
        style={{
          overflowX: "auto",
          overflowY: "auto",
          borderRadius: mobileMode ? 0 : 16,
          border: mobileMode ? "none" : "1px solid rgba(255,255,255,0.08)",
          background: "rgba(18,18,22,0.98)",
          maxHeight: mobileMode ? undefined : "min(58vh, 760px)",
          flex: mobileMode ? "1 1 auto" : undefined,
          minHeight: 0,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
        className="playerGameLogScroll"
      >
        <table
          style={{
            width: "max-content",
            minWidth: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "auto",
          }}
        >
          <colgroup>
            {columns.map((column, index) => (
              <col
                key={`career-col-${column.key}`}
                style={
                  index === 0
                    ? { width: `${firstColumnWidth}px` }
                    : index === 1
                      ? { width: `${secondColumnWidth}px` }
                    : mobileMode
                      ? { width: `${statColumnWidth}px` }
                      : undefined
                }
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={column.key}
                  style={{
                    padding: cellPadding,
                    textAlign: column.align ?? "center",
                    fontSize: headerFontSize,
                    fontWeight: 900,
                    color: "rgba(255,255,255,0.72)",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    background: "#101014",
                    position: "sticky",
                    top: 0,
                    zIndex: index === 0 ? 5 : index === 1 ? 4 : 3,
                    whiteSpace: "nowrap",
                    left: index === 0 ? 0 : index === 1 ? firstColumnWidth : undefined,
                    boxShadow:
                      index === 1
                        ? "1px 0 0 rgba(255,255,255,0.08), 8px 0 14px rgba(0,0,0,0.22)"
                        : index === 0
                          ? "1px 0 0 rgba(255,255,255,0.06)"
                          : undefined,
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={row.season}
                style={{
                  background: rowIndex % 2 === 0 ? "rgba(0,0,0,0.34)" : "rgba(35,35,35,0.9)",
                }}
              >
                {columns.map((column, columnIndex) => {
                  const rawValue = column.getValue(row, displayMode, selectedScoring);
                  const numericValue = typeof rawValue === "number" ? rawValue : undefined;
                  const heatmapStyle = getHeatmapCellStyle(
                    !PLAYER_PROFILE_STAT_COLOR_CODING_ENABLED || columnIndex <= 1
                      ? null
                      : getHeatmapTier(heatmapScalesBySeason[row.season]?.[column.key], numericValue)
                  );
                  const displayValue = (column.formatter ?? ((value) => formatMetric(value, column.digits ?? 1)))(rawValue);

                  return (
                    <td
                      key={`${row.season}-${column.key}`}
                      style={{
                        padding: cellPadding,
                        textAlign: column.align ?? "center",
                        fontSize: cellFontSize,
                        fontWeight: columnIndex === 0 ? 900 : 800,
                        color: "#fff",
                        whiteSpace: "nowrap",
                        position: columnIndex === 0 || columnIndex === 1 ? "sticky" : undefined,
                        left: columnIndex === 0 ? 0 : columnIndex === 1 ? firstColumnWidth : undefined,
                        zIndex: columnIndex === 0 ? 2 : columnIndex === 1 ? 1 : undefined,
                        background:
                          columnIndex === 0 || columnIndex === 1
                            ? rowIndex % 2 === 0
                              ? "#09090d"
                              : "#2a2a2a"
                            : undefined,
                        boxShadow:
                          columnIndex === 1
                            ? "1px 0 0 rgba(255,255,255,0.06), 8px 0 14px rgba(0,0,0,0.18)"
                            : columnIndex === 0
                              ? "1px 0 0 rgba(255,255,255,0.04)"
                              : undefined,
                      }}
                    >
                      {columnIndex === 0 || columnIndex === 1 ? (
                        rawValue
                      ) : (
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "max-content",
                            borderRadius: statPillRadius,
                            padding: statPillPadding,
                            background: heatmapStyle.background,
                            color: heatmapStyle.color,
                            fontSize: cellFontSize,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {displayValue}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
